// REACT IMPORTS
import React, { useState, useEffect, useContext } from "react";

// CSS
import "./RFPUserFormLog.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { getAPICall, postAPICall } from "../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../context/UserContext";

// MUI COMPONENTS
import { CircularProgress, IconButton } from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

// CUSTOM COMPONENTS
import J2NoResultsFound from "../../Misc/J2NoResultsFound/J2NoResultsFound";
import TanStackTable from "../../Misc/Table/TanStackTable";
import RFPUserFormLogDetails from "./RFPUserFormLogDetails/RFPUserFormLogDetails";

// CONSTANTS
import {
	dateSort,
	defaultSort,
	notApplicable,
} from "../../Misc/Table/TanStackTableConstants";
import { convertToPST } from "../../../constants/dateTime";

// REACT COMPONENT
const RFPUserFormLog = (props) => {
	// MSAL
	const { instance, accounts } = useMsal();

	// CONTEXT
	const employeeID = useContext(UserContext);

	// LOGS STATE
	const [rfpFormLogs, setRFPFormLogs] = useState([]);

	const [rfpFormLogsColumns, setRFPFormLogsColumns] = useState([]);
	const [rfpFormLogsRows, setRFPFormLogsRows] = useState([]);

	const [detailsOpen, setDetailsOpen] = useState(false);
	const [recordToView, setRecordToView] = useState(null);

	// LOADING STATES
	const [isLoading, setIsLoading] = useState(false);

	// USE EFFECT
	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "RFP User Form Log",
			employeeID: employeeID,
			notes: "Accessed the RFP User Form Log page",
		}).catch(() => {});
	}, []);

	useEffect(() => {
		fetchRFPFormLogs();
	}, []);

	useEffect(() => {
		createColumnData();
		createRowData();
	}, [rfpFormLogs]);

	// INPUT HANDLERS

	// API HELPER FUNCTIONS
	const fetchRFPFormLogs = async () => {
		setIsLoading(true);

		try {
			const response = await getAPICall(
				instance,
				accounts[0],
				"/api/logs/rfp",
			);
			setRFPFormLogs(response.data);
		} catch (error) {
			console.error(error);
		}

		setIsLoading(false);
	};

	const createColumnData = () => {
		setRFPFormLogsColumns([
			// submission details
			{
				accessorKey: "submitted_at",
				header: () => <span>Submitted At</span>,
				label: "Submitted At",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "date",
				filterFn: dateSort,
			},
			{
				accessorKey: "submitted_by_employee_name",
				header: () => <span>Submitted By</span>,
				label: "Submitted By",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			{
				accessorKey: "deal_name",
				header: () => <span>Deal Name</span>,
				label: "Deal Name",
				cell: (props) => (
					<span>{props.getValue() || notApplicable}</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			// error
			{
				accessorKey: "error_message",
				header: () => <span>Error Message</span>,
				label: "Error Message",
				cell: (props) => (
					<span
						style={{
							color: props.getValue() ? "red" : "black",
						}}
					>
						{props.getValue() || notApplicable}
					</span>
				),
				filterType: "textfield",
				filterFn: defaultSort,
			},
			// more details button
			{
				accessorKey: "moreDetails",
				header: "",
				label: "More Details",
				cell: ({ row }) => (
					<IconButton
						onClick={() => detailsClicked(row.original.record)}
						disableRipple={true}
						sx={{
							justifyContent: "flex-end",
						}}
					>
						<MoreHorizIcon fontSize="small" />
					</IconButton>
				),
				enableColumnFilter: false,
				enableSorting: false, // sorting is true by default
			},
		]);
	};

	const createRowData = () => {
		setRFPFormLogsRows(
			rfpFormLogs.map((log) => ({
				// submission details
				submitted_at: convertToPST(log.submitted_time),
				submitted_by_employee_name: log.submitted_by_employee_name,
				deal_name:
					log.property_name && log.specific_component && log.service
						? `${log.property_name}: ${log.specific_component}: ${log.service}`
						: notApplicable,
				// error
				error_message: log.error_message,
				// more details button
				record: log,
			})),
		);
	};

	// HELPER FUNCTIONS
	const detailsClicked = (record) => {
		setRecordToView(record);
		setDetailsOpen(true);
	};

	// RENDER
	return (
		<div className="RFPUserFormLog">
			<div className="RFPUserFormLogHeader">
				<div className="RFPUserFormLogHeaderTitle">
					RFP Submission Logs
				</div>
			</div>
			{isLoading && (
				<div className="loadingDiv">
					<CircularProgress color="primary" />
				</div>
			)}
			{!isLoading && (
				<div className="tableContainerDiv">
					{rfpFormLogs.length === 0 && <J2NoResultsFound />}
					{rfpFormLogs.length > 0 &&
						rfpFormLogsColumns.length > 0 &&
						rfpFormLogsRows.length > 0 && (
							<TanStackTable
								columnData={rfpFormLogsColumns}
								rowData={rfpFormLogsRows}
							/>
						)}
				</div>
			)}
			{detailsOpen && recordToView && (
				<RFPUserFormLogDetails
					open={detailsOpen}
					recordToView={recordToView}
					setOpen={setDetailsOpen}
				/>
			)}
		</div>
	);
};

export default RFPUserFormLog;
